<template>
  <div v-intersection-observer="{}" @viewport-entered="viewportEntered = true">
    <fcis-summary-widget
      v-if="shouldRender"
      class="emh-summary-widget"
      :environment.attr="environment"
      :standalone.attr="standalone"
      :parameters="fcisParameters"
      @fcis_summary_error="onFcisSummaryError"
      @fcis_summary_quote="onFcisSummaryQuote"
    >
      <div :class="{ 'emh-summary-widget__content': true, 'emh-summary-widget__content--inline': mode === 'inline' }">
        <template v-if="mode === 'block'">
          <p v-if="hasHeadline" class="wb-vehicle-tile__price-headline">
            <fcis-summary-text class="emh-summary-widget__label" :type.attr="'label'"></fcis-summary-text>
          </p>
          <p class="wb-vehicle-tile__price-value">
            <fcis-summary-text class="emh-summary-widget__label" :type.attr="'rate'"></fcis-summary-text>
            <fcis-summary-popup @fcis_summary_popup_toggle="onFcisPopupToggle">
              <slot name="popup-header"></slot>
            </fcis-summary-popup>
          </p>
        </template>
        <template v-else>
          <span class="wb-vehicle-tile__price-value">
            <fcis-summary-text class="emh-summary-widget__label" :type.attr="'rate'"></fcis-summary-text>
          </span>
          <span class="wb-vehicle-tile__price-value">
            <fcis-summary-text class="emh-summary-widget__label" :type.attr="'label'"></fcis-summary-text>
          </span>
          <span>
            <fcis-summary-popup @fcis_summary_popup_toggle="onFcisPopupToggle">
              <slot name="popup-header"></slot>
            </fcis-summary-popup>
          </span>
        </template>
      </div>
    </fcis-summary-widget>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, toRefs } from 'vue';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import { IntersectionObserverDirective } from '../../directives/intersectionObserver';
import { useFcisWidget } from './finance.composable';
import { computed } from 'vue';
import { ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import Vehicle from '@dh-io-emh/shared/src/models/vehicle.model';
import { WheelOrderEntry } from '../../types/wheels.type';

export default defineComponent({
  name: 'FinanceSummary',

  directives: {
    intersectionObserver: IntersectionObserverDirective,
  },

  props: {
    product: {
      type: Object as PropType<Vehicle | ProductDetail>,
      required: false,
      default: undefined,
    },
    lazyLoad: {
      type: Boolean,
      default: false,
    },
    financialCode: {
      type: String,
      required: false,
      default: undefined,
    },
    customerType: {
      type: String as PropType<CONTEXT_TYPES>,
      required: false,
      default: undefined,
    },
    mode: {
      type: String as PropType<'inline' | 'block'>,
      default: 'block',
    },
    standalone: {
      type: Boolean,
      default: true,
    },
    postCode: {
      type: String,
      required: false,
      default: undefined,
    },
    hasHeadline: {
      type: Boolean,
      required: false,
      default: true,
    },
    wheels: {
      type: Array as PropType<WheelOrderEntry[]>,
      required: false,
      default: () => [],
    },
  },

  emits: ['fcis-quote', 'fcis-error', 'fcis-popup-open', 'fcis-popup-close', 'loading'],

  setup(props, { emit }) {
    const { product, financialCode, customerType, postCode, wheels } = toRefs(props);
    const viewportEntered = ref(false);

    const onFcisPopupToggle = ({ detail: { state } }: CustomEvent<{ state: boolean }>) =>
      state ? emit('fcis-popup-open') : emit('fcis-popup-close');

    const onFcisSummaryError = (evt: any) => emit('fcis-error', evt);

    const onFcisSummaryQuote = (evt: any) => emit('fcis-quote', evt);

    const initialize = computed(
      () => props.standalone && (!props.lazyLoad || (props.lazyLoad && viewportEntered.value)),
    );

    const { environment, fcisParameters } = useFcisWidget({
      product,
      financialCode,
      customerType,
      initialize,
      isSummaryWidget: true,
      postCode,
      wheels,
      emit,
    });

    const shouldRender = computed(() => {
      // If standalone is false then this summary widget is connected to a
      // calculator widget on the same page and can be rendered right away.
      if (!props.standalone) {
        return true;
      }

      // Parameters might not be available immediately due to async calls (e.g. PLS)
      if (fcisParameters.value) {
        return true;
      }

      return false;
    });

    return {
      fcisParameters,
      viewportEntered,
      environment,
      onFcisPopupToggle,
      onFcisSummaryError,
      onFcisSummaryQuote,
      shouldRender,
    };
  },
});
</script>

<style lang="scss">
.emh-summary-widget {
  &__content {
    .wb-vehicle-tile__price-value {
      display: flex;
      align-items: center;
    }

    .wb-vehicle-tile__price-value sup {
      top: -5px !important;
      margin-left: 3px;
    }

    .wb-vehicle-tile__price:only-child > .wb-vehicle-tile__price-value {
      justify-content: center;
    }

    &--inline {
      display: inline-flex;
      align-items: center;

      .wb-vehicle-tile__price-value:first-child {
        margin-right: var(--wb-spacing-3xs);
      }
    }
  }
}
</style>
